// React and related imports
import React from 'react';

// Date Picker related imports
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import { ar } from 'date-fns/locale/ar';

// Redux selectors, hooks, and actions
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';
import { useAppSelector } from '../../redux/hooks/useAppSelector';

// i18 Translation
import { TFunction } from 'i18next';

// Types and Interfaces
import { ContextState } from '../../hooks/useContentContextState';

// Custom Components
import { DatePickerInput } from '../Shared/DataPickerInput/DatePickerInput';

// Register Arabic locale for date-fns
registerLocale('ar', ar);

interface DateRangePickerProps {
  t: TFunction<'translation', undefined>;
  contextState: ContextState;
  updateField: (field: keyof ContextState, value: string) => void;
  validateField: (field: string, value: string) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  t,
  contextState,
  updateField,
  validateField,
}) => {
  const isRtl = useAppSelector(selectIsRTL);
  const locale = isRtl ? 'ar' : '';

  const getDateObject = (dateString: string | undefined): Date | undefined =>
    dateString ? new Date(dateString) : undefined;

  const getDatesBetween = (start: Date, end: Date) => {
    const dates = [];
    const current = new Date(start);
    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const handleDateChange = (field: keyof ContextState, date: Date | null) => {
    const dateString = date ? date.toDateString() : '';
    updateField(field, dateString);
    if (!contextState?.isForever) {
      validateField(field, dateString);
    }
  };

  return (
    <div id="date-range-picker" className="flex items-center">
      {/* Start Date Picker */}
      <div className="relative">
        <DatePickerInput
          selected={getDateObject(contextState?.startDate)}
          selectedDays={getDatesBetween(
            getDateObject(contextState?.startDate) || new Date(),
            getDateObject(contextState?.endDate) || new Date()
          )}
          onChange={(date) =>
            handleDateChange('startDate' as keyof ContextState, date)
          }
          placeholder={t('contentContext.selectStartDate')}
          minDate={new Date()}
          maxDate={getDateObject(contextState?.endDate)}
          locale={locale}
        />
      </div>

      {/* Separator */}
      <span className="mx-4 text-gray-500">{t('contentContext.to')}</span>

      {/* End Date Picker */}
      <div className="relative">
        <DatePickerInput
          selected={getDateObject(contextState?.endDate)}
          selectedDays={getDatesBetween(
            getDateObject(contextState?.startDate) || new Date(),
            getDateObject(contextState?.endDate) || new Date()
          )}
          onChange={(date) =>
            handleDateChange('endDate' as keyof ContextState, date)
          }
          placeholder={t('contentContext.selectEndDate')}
          minDate={getDateObject(contextState?.startDate) || new Date()}
          locale={locale}
        />
      </div>
    </div>
  );
};
